import { createContext, useEffect, useState } from 'react';
// routing
import Routes from 'routes';

// project imports
import Locales from 'ui-component/Locales';
import NavigationScroll from 'layout/NavigationScroll';
import RTLLayout from 'ui-component/RTLLayout';
import Snackbar from 'ui-component/extended/Snackbar';
import Notistack from 'ui-component/third-party/Notistack';
import ThemeCustomization from 'themes';
import { kc } from './keycloak';
import { Box, CircularProgress } from '@mui/material';

// Create a context to share authentication information (user data)
// across the whole application.
export const AuthProvider = createContext(null);

const App = () => {
  // State to hold user information once authenticated
  const [userInformation, SetUserInformation] = useState(null);

  // State to handle loading state while waiting for Keycloak init
  const [loading, setLoading] = useState(true);

  // Helper function to extract and set user information from Keycloak token
  const updateUserInformation = () => {
    try {
      if (kc.authenticated && kc.tokenParsed) {
        // If authenticated, extract user details from the token
        const user = {
          id: kc.tokenParsed.sub, // Unique user ID (subject)
          name: kc.tokenParsed.name, // User's full name
          token: kc.token, // Access token
          // Client roles from Keycloak (client id is d2d-admin-console).
          roles: kc.tokenParsed?.resource_access?.['d2d-admin-console']?.roles,
          isAdmin:
            kc.tokenParsed?.resource_access?.[
              'd2d-admin-console'
            ]?.roles?.includes('admin') ?? false,
          sub: kc.tokenParsed.sub, // User subject
          tokenParsed: kc.tokenParsed, // Entire parsed token object
          OrgUnit: kc.tokenParsed.OrgUnit, // Custom claim: organization unit i.e. User Attributes
          OrgUnitValue: kc.tokenParsed.OrgUnitValue // Custom claim: organizational unit value i.e. User Attributes
        };
        SetUserInformation(user);
      } else {
        // If login failed or user not authenticated
        SetUserInformation(null);
      }
    } catch (e) {
      // Catch any unexpected errors
      console.error('Error updating user information:', e);
      SetUserInformation(null);
    }
  };

  useEffect(() => {
    // Initialize Keycloak when the app starts
    kc.init({
      onLoad: 'login-required', // Automatically redirect to login if not logged in
      checkLoginIframe: false // Disable login check in the background iframe for simplicity
    })
      .then((auth) => {
        if (auth) {
          updateUserInformation();
        } else {
          // If login failed or user not authenticated
          SetUserInformation(null);
        }
        // Mark loading as finished
        setLoading(false);
      })
      .catch((error) => {
        // Handle initialization errors
        console.error('Keycloak initialization error:', error);
        SetUserInformation(null);
        setLoading(false);
      });

    // Handle token expiration – refresh 30 seconds before expiry
    kc.onTokenExpired = async () => {
      try {
        const refreshed = await kc.updateToken(30);
        if (refreshed) {
          // Update user information with the new token
          updateUserInformation();
        } else {
          // Token refresh failed, user will be redirected to login
          console.warn('Token refresh failed');
        }
      } catch (error) {
        // Token refresh failed, user will be redirected to login
        console.error('Token refresh error:', error);
        SetUserInformation(null);
      }
    };

    // Handle authentication errors
    kc.onAuthError = (error) => {
      console.error('Keycloak authentication error:', error);
      SetUserInformation(null);
    };

    // Handle logout
    kc.onAuthLogout = () => {
      SetUserInformation(null);
    };
  }, []);

  // While waiting for Keycloak authentication, show a loading spinner
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={6}>
        <CircularProgress />
      </Box>
    );
  }

  // Once loaded, provide user info via AuthProvider context
  return (
    <AuthProvider.Provider value={userInformation}>
      <ThemeCustomization>
        <RTLLayout>
          <Locales>
            <NavigationScroll>
              <>
                {/* Notification provider (snackbar system) */}
                <Notistack>
                  {/* App routes (your page navigation) */}
                  <Routes />
                  {/* Snackbar UI for showing alerts */}
                  <Snackbar />
                </Notistack>
              </>
            </NavigationScroll>
          </Locales>
        </RTLLayout>
      </ThemeCustomization>
    </AuthProvider.Provider>
  );
};

export default App;
